import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/v2/Toast";
import { useInviteTeammate, type InviteRole } from "@/lib/invitations";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * One-shot invite dialog used by Settings → Team. The onboarding
 * wizard has its own multi-row UX for bulk-inviting at signup; this
 * is the *"I forgot to invite Bob"* path post-onboarding. Same
 * backend endpoint either way.
 */
export function InviteTeammateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("MEMBER");
  const invite = useInviteTeammate();
  const toast = useToast();

  const trimmed = email.trim().toLowerCase();
  const validEmail = EMAIL_RE.test(trimmed);
  const canSubmit = validEmail && !invite.isPending;

  const reset = () => {
    setEmail("");
    setRole("MEMBER");
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    try {
      await invite.mutateAsync({ email: trimmed, role });
      toast.success(
        "Invitation sent",
        `${trimmed} will get an email to set their password.`,
      );
      reset();
      onOpenChange(false);
    } catch (e) {
      const msg = (e as Error).message ?? "invite_failed";
      if (msg.includes("plan_operator_limit_exceeded")) {
        toast.error(
          "You're at your seat cap",
          "Upgrade in Workspace → Billing for more operators.",
        );
        return;
      }
      const friendly = msg.includes("user_already_exists")
        ? "This email is already on the team."
        : msg.includes("invalid_role")
          ? "Pick a valid role."
          : msg.includes("email_required")
            ? "Email is required."
            : "Couldn't send the invitation. Try again.";
      toast.error("Invitation failed", friendly);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
          <DialogDescription>
            They'll get an email with a link to set their password and
            join your workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="invite-email" className="text-[13px] font-semibold">
              Email address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="invite-email"
              type="email"
              autoFocus
              autoComplete="off"
              placeholder="teammate@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) onSubmit();
              }}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="invite-role" className="text-[13px] font-semibold">
              Role
            </Label>
            <Select value={role} onValueChange={(v) => setRole(v as InviteRole)}>
              <SelectTrigger id="invite-role" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MEMBER">Member</SelectItem>
                <SelectItem value="ADMIN">Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              <em>Admin</em> can change workspace settings + billing.{" "}
              <em>Member</em> can manage bookings, clients, and inventory.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={invite.isPending}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit} className="gap-2">
            {invite.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {invite.isPending ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
